import { trackProductAction } from '@/libs/product-analytics';
import { Box, Button, Flex, Input, Separator, SimpleGrid, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AvatarUpload } from '@/components/shared/avatar-upload';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import type { UpdateUserProfileDto } from '@/services/user-service';
import { deleteUserAvatar, updateUserProfile, uploadUserAvatar } from '@/services/user-service';

const formSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
});

export type AccountInformationFormData = z.infer<typeof formSchema>;

export interface AccountInformationFormProps {
  defaultValues: Partial<AccountInformationFormData>;
  onSuccess?: () => void;
  onSubmitting?: (isSubmitting: boolean) => void;
  showActions?: boolean; // Whether to show action buttons
}

export interface AccountInformationFormRef {
  submit: () => void;
}

const AccountInformationForm = forwardRef<AccountInformationFormRef, AccountInformationFormProps>(
  ({ defaultValues, onSuccess, onSubmitting, showActions = false }, ref) => {
    const formRef = useRef<HTMLFormElement>(null);
    const queryClient = useQueryClient();
    const { refreshCurrentUser: fetchCurrentUser, user } = useAuth();
    const { t } = useTranslation();

    const {
      register,
      handleSubmit,
      reset,
      formState: { errors, isDirty },
    } = useForm<AccountInformationFormData>({
      resolver: zodResolver(formSchema),
      defaultValues: defaultValues as AccountInformationFormData,
    });

    const mutation = useMutation({
      mutationFn: async (data: AccountInformationFormData) => {
        return trackProductAction('profile', 'update', async () => {
          return await updateUserProfile(data as UpdateUserProfileDto);
        });
      },
      onSuccess: async (_response, variables) => {
        await fetchCurrentUser();
        queryClient.invalidateQueries({ queryKey: ['user'] });
        reset(variables);

        toaster.success({
          title: t('settings.accountPages.profile.successTitle'),
          description: t('settings.accountPages.profile.successDescription'),
        });

        onSuccess?.();
      },
      onError: (error) => {
        console.error('Failed to update profile:', error);
        toaster.error({
          title: t('common.error'),
          description: t('settings.accountPages.profile.errorDescription'),
        });
      },
    });

    useEffect(() => {
      onSubmitting?.(mutation.isPending);
    }, [onSubmitting, mutation.isPending]);

    const onSubmit = (data: AccountInformationFormData) => {
      mutation.mutate(data);
    };

    useImperativeHandle(ref, () => ({
      submit: () => {
        formRef.current?.requestSubmit();
      },
    }));

    return (
      <Box as="form" ref={formRef} onSubmit={handleSubmit(onSubmit)} maxW="720px" w="full">
        <AvatarUpload
          initialImageUrl={user?.avatar?.fileUrl}
          size="sm"
          customUploadFn={uploadUserAvatar}
          customDeleteFn={deleteUserAvatar}
          queryKey={['user']}
          label={t('settings.avatarUpload.avatar')}
        />
        <Box py={6}>
          <Separator />
        </Box>
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} mb={4}>
          <Field
            label={t('settings.accountPages.profile.firstName')}
            errorText={errors.firstName?.message}
          >
            <Input
              {...register('firstName')}
              variant="outline"
              placeholder={t('settings.accountPages.profile.firstNamePlaceholder')}
            />
          </Field>

          <Field
            label={t('settings.accountPages.profile.lastName')}
            errorText={errors.lastName?.message}
          >
            <Input
              {...register('lastName')}
              variant="outline"
              placeholder={t('settings.accountPages.profile.lastNamePlaceholder')}
            />
          </Field>
        </SimpleGrid>

        <Field label={t('common.email')} mb={4}>
          <Input value={user?.email || ''} readOnly variant="outline" />
          <Text fontSize="xs" color="gray.500" mt={1}>
            {t('settings.accountPages.profile.emailHint')}
          </Text>
        </Field>

        {showActions && (
          <Flex gap={2} mt={4}>
            <Button type="submit" size="sm" loading={mutation.isPending} disabled={!isDirty}>
              {t('common.update')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!isDirty || mutation.isPending}
              onClick={() => reset(defaultValues as AccountInformationFormData)}
            >
              {t('common.cancel')}
            </Button>
          </Flex>
        )}
      </Box>
    );
  }
);

AccountInformationForm.displayName = 'AccountInformationForm';

export default AccountInformationForm;
